import React, { useEffect, useState } from "react";
import {
  DigitalModelForm,
  DigitalModelProps,
} from "../../../types/UploadFieldsType";
import TextInput from "../formComp/TextInputNew";
import SelectInput from "../formComp/SelectInputNew";

const DigitalModel: React.FC<DigitalModelProps> = ({ onChange }) => {
  const [form, setForm] = useState<DigitalModelForm>({
    neededFor: "",
    modelType: "",
    dieRequirement: "",
    otherPreferences: "", // Stored as comma-separated string
    otherSpecifications: "",
  });

  const handleChange = (field: keyof DigitalModelForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    onChange(form);
  }, [form, onChange]);

  const checkboxOptions = [
    "Articulator",
    "Vertex cup attachment",
    "Quadrant",
    "Full arch",
    "Keep palate intact",
  ];

  const selectedPreferences = form.otherPreferences
    ? form.otherPreferences.split(",").map((s) => s.trim())
    : [];

  return (
    <div className="w-full text-[var(--cu-text)]">
      <div className="mb-3 border-b border-[var(--cu-border)] pb-2">
        <h2 className="text-sm font-medium text-[var(--cu-accent)]">
          Digital Model
        </h2>
      </div>

      <div className="w-full space-y-3">
        <SelectInput
          label="Design needed for:"
          name="neededFor"
          value={form.neededFor}
          onChange={(e) => handleChange("neededFor", e.target.value)}
          options={[
            { value: "Simple digital model", label: "Simple digital model" },
            { value: "Ortho study model", label: "Ortho study model" },
            {
              value: "Model for bleaching tray",
              label: "Model for bleaching tray",
            },
          ]}
        />

        <SelectInput
          label="Model type"
          name="modelType"
          value={form.modelType}
          onChange={(e) => handleChange("modelType", e.target.value)}
          options={[
            { value: "Solid", label: "Solid" },
            { value: "Hollow", label: "Hollow" },
          ]}
        />

        <SelectInput
          optional
          label="Die requirement"
          name="dieRequirement"
          value={form.dieRequirement}
          onChange={(e) => handleChange("dieRequirement", e.target.value)}
          options={[
            {
              value: "Unsectioned model without die",
              label: "Unsectioned model without die",
            },
            {
              value: "Unsectioned model with separate die",
              label: "Unsectioned model with separate die",
            },
            {
              value: "Unsectioned model with margin ditched",
              label: "Unsectioned model with margin ditched",
            },
            {
              value: "Unsectioned model with margin ditched without die",
              label: "Unsectioned model with margin ditched without die",
            },
            {
              value: "Unsectioned model with margin ditched with separate die",
              label: "Unsectioned model with margin ditched with separate die",
            },
            {
              value: "Sectioned model with removable die",
              label: "Sectioned model with removable die",
            },
          ]}
        />

        <div className="mb-5">
          <label className="mb-1 block text-sm font-medium text-[var(--cu-muted)]">
            Other preferences
            <span className="ml-2 text-[9px] font-medium normal-case tracking-normal text-[var(--cu-subtle)]">
              Optional
            </span>
          </label>
          <p className="mb-2 text-xs text-[var(--cu-subtle)]">
            Please select all that apply
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {checkboxOptions.map((option) => {
              const isChecked = selectedPreferences.includes(option);

              return (
                <label
                  key={option}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-3 text-sm transition-all duration-300 ${
                    isChecked
                      ? "border-[var(--cu-accent)]/50 bg-[var(--cu-accent-soft)] text-[var(--cu-accent)]"
                      : "border-[var(--cu-border)] bg-[var(--cu-input)] text-[var(--cu-muted)] hover:border-[var(--cu-accent)]/40"
                  }`}
                >
                  <input
                    type="checkbox"
                    value={option}
                    checked={isChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      let updatedPreferences: string[];

                      if (checked) {
                        updatedPreferences = [...selectedPreferences, option];
                      } else {
                        updatedPreferences = selectedPreferences.filter(
                          (item) => item !== option
                        );
                      }

                      setForm((prev) => ({
                        ...prev,
                        otherPreferences: updatedPreferences.join(", "),
                      }));
                    }}
                    className="accent-[#2fa9bf]"
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        </div>

        <TextInput
          optional
          label="Other specifications"
          name="otherSpecifications"
          value={form.otherSpecifications}
          onChange={(e) => handleChange("otherSpecifications", e.target.value)}
        />
      </div>
    </div>
  );
};

export default DigitalModel;
